import React, { useState, useEffect, useCallback, useContext, useRef, useMemo } from "react";
import {
  CalendarDays, ChevronLeft, ChevronRight, Plus, MapPin, Clock,
  User, Package, Pencil, Trash2, X, Check, Loader2, Navigation,
  AlertTriangle, RefreshCw, ShoppingCart, Ruler, Tag, ExternalLink,
  ChevronDown, Bell, Volume2, History, Sun, Filter, Phone, AlarmClock,
  Wrench, Hammer, Scissors,
} from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { AuthCtx } from "../appContexts";

// ─── Sabitler ───────────────────────────────────────────────────────────────
const WEEKDAYS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
const MONTHS_TR = [
  "Ocak","Şubat","Mart","Nisan","Mayıs","Haziran",
  "Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"
];

// Üçlü Kategori Sistemi: Montaj (Yeşil), Ölçü (Mavi), Tamir (Kırmızı)
export const APPT_TYPE = {
  montaj: { label: "Montaj",  icon: Hammer,   bg: "bg-emerald-500", dot: "bg-emerald-500", light: "bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300", pill: "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-700" },
  olcu:   { label: "Ölçü",    icon: Scissors, bg: "bg-sky-500",     dot: "bg-sky-500",     light: "bg-sky-50 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300",     pill: "bg-sky-100 text-sky-800 border-sky-300 dark:bg-sky-900/20 dark:text-sky-300 dark:border-sky-700" },
  tamir:  { label: "Tamir",   icon: Wrench,   bg: "bg-rose-500",    dot: "bg-rose-500",    light: "bg-rose-50 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300",    pill: "bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-700" },
  genel:  { label: "Genel",   icon: CalendarDays, bg: "bg-slate-500", dot: "bg-slate-500", light: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300", pill: "bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-600" },
};

export const APPT_STATUS = {
  bekliyor:         { label: "Bekliyor",          color: "sky",     bg: "bg-sky-500",     light: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300",     dot: "bg-sky-500" },
  yolda:            { label: "Yolda",              color: "amber",   bg: "bg-amber-500",   light: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",   dot: "bg-amber-500" },
  montaj_yapiliyor: { label: "Montaj Yapılıyor",   color: "violet",  bg: "bg-violet-500",  light: "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300",  dot: "bg-violet-500" },
  bitti:            { label: "Bitti",              color: "emerald", bg: "bg-emerald-500", light: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300", dot: "bg-emerald-500" },
  ertelendi:        { label: "Ertelendi",          color: "rose",    bg: "bg-rose-500",    light: "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300",    dot: "bg-rose-500" },
};

const STATUS_ORDER = ["bekliyor","yolda","montaj_yapiliyor","bitti","ertelendi"];

// ─── Yardımcılar ────────────────────────────────────────────────────────────
function toDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}
function todayStr() { return toDateStr(new Date()); }

function openMaps(address) {
  const q = encodeURIComponent(address);
  window.open(`https://www.google.com/maps/search/?api=1&query=${q}`, "_blank");
}

// ─── Takvim gün oluşturma ───────────────────────────────────────────────────
function buildMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const last  = new Date(year, month + 1, 0);
  let dow = first.getDay(); // 0=Sun
  dow = dow === 0 ? 6 : dow - 1; // Pzt=0
  const cells = [];
  for (let i = dow - 1; i >= 0; i--) {
    cells.push({ date: new Date(year, month, -i), cur: false });
  }
  for (let d = 1; d <= last.getDate(); d++) {
    cells.push({ date: new Date(year, month, d), cur: true });
  }
  while (cells.length % 7 !== 0 || cells.length < 35) {
    const prev = cells[cells.length - 1].date;
    const nd = new Date(prev); nd.setDate(nd.getDate() + 1);
    cells.push({ date: nd, cur: false });
  }
  return cells;
}

function buildWeekDays(baseDate) {
  // Haftanın Pazartesi günü
  const d = new Date(baseDate);
  const dow = d.getDay();
  const offset = dow === 0 ? -6 : 1 - dow; // Pzt=0
  d.setDate(d.getDate() + offset);
  return Array.from({ length: 7 }, (_, i) => {
    const nd = new Date(d); nd.setDate(nd.getDate() + i);
    return nd;
  });
}

// ─── Randevu Pill ───────────────────────────────────────────────────────────
function ApptPill({ appt, onClick, onDragStart, compact = false }) {
  const tp = APPT_TYPE[appt.type] || APPT_TYPE.genel;
  const isPostponed = appt.status === "ertelendi";
  return (
    <div
      draggable
      onDragStart={e => { e.stopPropagation(); onDragStart(appt.id); }}
      onClick={e => { e.stopPropagation(); onClick(appt); }}
      data-testid={`appt-pill-${appt.id}`}
      className={`group cursor-pointer select-none rounded-md px-1.5 py-0.5 text-[11px] font-semibold leading-tight
                  truncate transition-all hover:opacity-90 hover:shadow-md active:scale-[0.97]
                  ${tp.pill} border ${isPostponed ? "opacity-60 line-through" : ""}`}
      title={`[${tp.label}] ${appt.startTime} — ${appt.customerName}: ${appt.title}`}
    >
      {!compact && <span className="mr-1 opacity-70">{appt.startTime}</span>}
      {appt.customerName || appt.title}
    </div>
  );
}

// ─── Form Modal (Yeni/Düzenle) ───────────────────────────────────────────────
function ApptFormModal({ open, onClose, onSaved, editing, prefillDate }) {
  const [form, setForm] = useState({
    title: "", customerId: "", customerName: "", orderId: "",
    date: prefillDate || todayStr(), startTime: "09:00", endTime: "11:00",
    address: "", phone: "", description: "", status: "bekliyor", notes: "",
    type: "montaj", alarmMinutes: [120],
  });
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [custSearch, setCustSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({ alarmMinutes: [120], ...editing });
      setCustSearch(editing.customerName || "");
    } else {
      setForm(f => ({ ...f, date: prefillDate || todayStr(), type: "montaj", alarmMinutes: [120] }));
      setCustSearch("");
    }
    api.get("/customers").then(r => setCustomers(r.data || [])).catch(() => {});
  }, [open, editing, prefillDate]);

  useEffect(() => {
    if (!form.customerId) { setOrders([]); return; }
    api.get("/orders").then(r => {
      setOrders((r.data || []).filter(o => o.customerId === form.customerId));
    }).catch(() => {});
  }, [form.customerId]);

  const filteredCusts = useMemo(() =>
    custSearch.length < 2 ? [] : customers.filter(c =>
      (c.name || "").toLowerCase().includes(custSearch.toLowerCase())
    ).slice(0, 8),
    [custSearch, customers]
  );

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleAlarm = (min) => setForm(f => {
    const arr = f.alarmMinutes || [];
    return { ...f, alarmMinutes: arr.includes(min) ? arr.filter(x => x !== min) : [...arr, min] };
  });

  const save = async () => {
    if (!form.date) { toast.error("Tarih zorunludur"); return; }
    if (!form.customerName) { toast.error("Müşteri adı zorunludur"); return; }
    setLoading(true);
    try {
      if (editing) {
        await api.put(`/appointments/${editing.id}`, form);
        toast.success("Randevu güncellendi");
      } else {
        await api.post("/appointments", form);
        toast.success("Randevu oluşturuldu");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="zen-panel w-full max-w-lg max-h-[90vh] overflow-y-auto zen-anim-up" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-[#22304A]">
          <h3 className="font-bold text-base">{editing ? "Randevu Düzenle" : "Yeni Randevu"}</h3>
          <button onClick={onClose} className="zen-btn-ghost !p-1.5"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          {/* Kategori Seçici — Montaj | Ölçü | Tamir | Genel */}
          <div>
            <label className="zen-label">Kategori</label>
            <div className="grid grid-cols-4 gap-1.5">
              {Object.entries(APPT_TYPE).map(([key, tp]) => {
                const Icon = tp.icon;
                return (
                  <button key={key} type="button" onClick={() => set("type", key)}
                    data-testid={`appt-type-${key}`}
                    className={`flex flex-col items-center gap-1 py-2 px-1 rounded-xl border-2 text-[11px] font-bold transition-all
                      ${form.type === key ? `${tp.light} border-current/40 scale-[1.03]` : "border-slate-200 dark:border-[#22304A] text-slate-400 hover:border-slate-300"}`}>
                    <Icon className="w-4 h-4" />
                    {tp.label}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Başlık */}
          <div>
            <label className="zen-label">Başlık / Not</label>
            <input value={form.title} onChange={e => set("title", e.target.value)}
              placeholder="Ürün adı veya kısa not" className="zen-input w-full" data-testid="appt-title" />
          </div>
          {/* Müşteri */}
          <div className="relative">
            <label className="zen-label">Müşteri *</label>
            <input value={custSearch}
              onChange={e => { setCustSearch(e.target.value); set("customerName", e.target.value); set("customerId", ""); set("orderId", ""); }}
              placeholder="Müşteri adı ara..." className="zen-input w-full" data-testid="appt-customer" />
            {filteredCusts.length > 0 && (
              <div className="absolute z-50 w-full mt-1 zen-panel shadow-xl max-h-48 overflow-y-auto">
                {filteredCusts.map(c => (
                  <button key={c.id} onClick={() => {
                    set("customerId", c.id); set("customerName", c.name);
                    set("address", c.address || form.address || "");
                    set("phone", c.phone || c.tel || form.phone || "");
                    setCustSearch(c.name);
                  }}
                    className="w-full text-left px-3 py-2 hover:bg-slate-100 dark:hover:bg-[#22304A]">
                    <div className="text-sm font-semibold">{c.name}</div>
                    <div className="text-xs text-slate-400 flex gap-3 mt-0.5">
                      {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3"/>{c.phone}</span>}
                      {c.address && <span className="truncate">{c.address}</span>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Telefon + Adres */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="zen-label">Telefon</label>
              <input type="tel" value={form.phone || ""} onChange={e => set("phone", e.target.value)}
                placeholder="0555 123 45 67" className="zen-input w-full" data-testid="appt-phone" />
            </div>
            <div>
              <label className="zen-label">Adres</label>
              <div className="flex gap-1">
                <input value={form.address} onChange={e => set("address", e.target.value)}
                  placeholder="Montaj adresi" className="zen-input flex-1" data-testid="appt-address" />
                {form.address && (
                  <button onClick={() => openMaps(form.address)} className="zen-btn-ghost !px-2 text-sky-500" title="Haritayı Aç">
                    <Navigation className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          </div>
          {/* Sipariş bağlantısı */}
          {orders.length > 0 && (
            <div>
              <label className="zen-label">Sipariş Bağla (opsiyonel)</label>
              <select value={form.orderId || ""} onChange={e => set("orderId", e.target.value)} className="zen-input w-full" data-testid="appt-order">
                <option value="">— Sipariş bağlama —</option>
                {orders.map(o => (
                  <option key={o.id} value={o.id}>
                    {o.orderNo ? `SP-${String(o.orderNo).padStart(4,"0")}` : o.id} · {o.customerName}
                  </option>
                ))}
              </select>
            </div>
          )}
          {/* Tarih + Saat */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="zen-label">Tarih</label>
              <input type="date" value={form.date} onChange={e => set("date", e.target.value)} className="zen-input w-full" data-testid="appt-date" />
            </div>
            <div>
              <label className="zen-label">Başlangıç</label>
              <input type="time" value={form.startTime} onChange={e => set("startTime", e.target.value)} className="zen-input w-full" data-testid="appt-start" />
            </div>
            <div>
              <label className="zen-label">Bitiş</label>
              <input type="time" value={form.endTime} onChange={e => set("endTime", e.target.value)} className="zen-input w-full" data-testid="appt-end" />
            </div>
          </div>
          {/* Sesli Alarm Zamanları */}
          <div>
            <label className="zen-label flex items-center gap-1.5"><Volume2 className="w-3.5 h-3.5 text-amber-500" />Sesli Alarm (randevudan önce)</label>
            <div className="flex gap-2 flex-wrap">
              {[15, 30, 60, 120, 1440].map(min => {
                const lbl = min < 60 ? `${min}dk` : min < 1440 ? `${min/60}sa` : "1 gün";
                const active = (form.alarmMinutes || []).includes(min);
                return (
                  <button key={min} type="button" onClick={() => toggleAlarm(min)} data-testid={`alarm-${min}`}
                    className={`px-3 py-1 rounded-lg text-[11px] font-bold border transition-all
                      ${active ? "bg-amber-500 text-white border-amber-500" : "border-slate-300 dark:border-slate-700 text-slate-500 hover:border-amber-400"}`}>
                    {lbl}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Durum */}
          <div>
            <label className="zen-label">Durum</label>
            <select value={form.status} onChange={e => set("status", e.target.value)} className="zen-input w-full" data-testid="appt-status">
              {STATUS_ORDER.map(s => <option key={s} value={s}>{APPT_STATUS[s].label}</option>)}
            </select>
          </div>
          {/* Not */}
          <div>
            <label className="zen-label">Açıklama / Not</label>
            <textarea value={form.notes} onChange={e => set("notes", e.target.value)}
              rows={2} placeholder="İç notlar..." className="zen-input w-full !text-xs" data-testid="appt-notes" />
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-slate-200 dark:border-[#22304A]">
          <button onClick={onClose} className="zen-btn-ghost">Vazgeç</button>
          <button onClick={save} disabled={loading} className="zen-btn-primary" data-testid="appt-save-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Kaydet
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Detay Modal ─────────────────────────────────────────────────────────────
function ApptDetailModal({ appt, onClose, onEdit, onDelete, onStatusChange, onSnooze, isAdmin }) {
  const [order, setOrder] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [showSnooze, setShowSnooze] = useState(false);
  const [snoozeDate, setSnoozeDate] = useState("");
  const [snoozeReason, setSnoozeReason] = useState("");
  const [snoozeSaving, setSnoozeSaving] = useState(false);
  const st = APPT_STATUS[appt.status] || APPT_STATUS.bekliyor;

  useEffect(() => {
    if (!appt.orderId) return;
    setLoadingOrder(true);
    api.get(`/orders`).then(r => {
      setOrder((r.data || []).find(o => o.id === appt.orderId) || null);
    }).catch(() => {}).finally(() => setLoadingOrder(false));
  }, [appt.orderId]);

  const changeStatus = async (newStatus) => {
    setChangingStatus(true);
    try {
      await onStatusChange(appt.id, newStatus);
    } finally { setChangingStatus(false); }
  };

  const dateLabel = appt.date
    ? new Date(`${appt.date}T12:00:00`).toLocaleDateString("tr-TR", { weekday:"long", day:"numeric", month:"long", year:"numeric" })
    : "-";

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="zen-panel w-full max-w-lg max-h-[90vh] overflow-y-auto zen-anim-up" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={`p-4 rounded-t-xl ${st.light} border-b border-current/10`}>
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className={`inline-flex items-center gap-1.5 text-xs font-bold px-2 py-1 rounded-full mb-2 ${st.light}`}>
                <span className={`w-2 h-2 rounded-full ${st.dot}`} />
                {st.label}
              </div>
              <h3 className="font-extrabold text-base leading-tight">{appt.customerName}</h3>
              {appt.title && <p className="text-sm opacity-80 mt-0.5">{appt.title}</p>}
            </div>
            <button onClick={onClose} className="zen-btn-ghost !p-1.5 shrink-0"><X className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="p-4 space-y-4">
          {/* Tarih & Saat */}
          <div className="grid grid-cols-2 gap-3">
            <div className="zen-panel !p-3 flex items-center gap-2.5">
              <CalendarDays className="w-4 h-4 text-slate-400 shrink-0" />
              <div>
                <div className="text-[10px] text-slate-400 font-semibold uppercase">Tarih</div>
                <div className="text-sm font-bold">{dateLabel}</div>
              </div>
            </div>
            <div className="zen-panel !p-3 flex items-center gap-2.5">
              <Clock className="w-4 h-4 text-slate-400 shrink-0" />
              <div>
                <div className="text-[10px] text-slate-400 font-semibold uppercase">Saat</div>
                <div className="text-sm font-bold">{appt.startTime} — {appt.endTime}</div>
              </div>
            </div>
          </div>

          {/* Adres */}
          {appt.address && (
            <div className="zen-panel !p-3 flex items-center gap-2.5">
              <MapPin className="w-4 h-4 text-rose-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] text-slate-400 font-semibold uppercase">Adres</div>
                <div className="text-sm font-semibold truncate">{appt.address}</div>
              </div>
              <button
                data-testid={`appt-maps-${appt.id}`}
                onClick={() => openMaps(appt.address)}
                className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-500 text-white text-xs font-bold hover:bg-sky-600 transition-colors"
              >
                <Navigation className="w-3 h-3" />
                Haritayı Aç
              </button>
            </div>
          )}

          {/* Durum Değiştir */}
          <div>
            <div className="text-[11px] font-bold text-slate-500 uppercase mb-2">Durumu Değiştir</div>
            <div className="flex flex-wrap gap-1.5">
              {STATUS_ORDER.map(s => {
                const ss = APPT_STATUS[s];
                const isActive = appt.status === s;
                return (
                  <button
                    key={s}
                    data-testid={`appt-status-${s}-${appt.id}`}
                    onClick={() => !isActive && changeStatus(s)}
                    disabled={isActive || changingStatus}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-all border
                      ${isActive ? `${ss.light} border-current/30 ring-1 ring-current/40` : "border-slate-200 dark:border-[#22304A] hover:border-slate-300 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-[#22304A]"}`}
                  >
                    <span className={`w-2 h-2 rounded-full ${ss.dot}`} />
                    {ss.label}
                    {isActive && <Check className="w-3 h-3" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Bağlı Sipariş */}
          {appt.orderId && (
            <div>
              <div className="text-[11px] font-bold text-slate-500 uppercase mb-2 flex items-center gap-1.5">
                <ShoppingCart className="w-3.5 h-3.5" />Bağlı Sipariş
              </div>
              {loadingOrder ? (
                <div className="flex items-center gap-2 text-xs text-slate-400 py-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />Yükleniyor...
                </div>
              ) : order ? (
                <div className="zen-panel !p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="zen-mono text-xs font-bold text-[#E5322A]">
                      {order.orderNo ? `SP-${String(order.orderNo).padStart(4,"0")}` : order.id}
                    </span>
                    <span className="text-xs font-bold">
                      {(order.total || 0).toLocaleString("tr-TR",{maximumFractionDigits:2})} {order.currency || "TL"}
                    </span>
                  </div>
                  {/* Ürün kalemleri */}
                  {(order.items || []).filter(it => !it.isAccessory).slice(0,4).map((it, i) => (
                    <div key={i} className="text-xs text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/30 rounded-lg p-2">
                      <div className="font-semibold flex items-center gap-1.5">
                        <Tag className="w-3 h-3 text-[#6D5CE6]" />
                        {it.product || "—"}
                        {it.color && <span className="text-slate-400">· {it.color}</span>}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-slate-500">
                        {(it.width && it.height) && (
                          <span className="flex items-center gap-1"><Ruler className="w-3 h-3" />{it.width}×{it.height} cm</span>
                        )}
                        {it.fabricCode && <span className="flex items-center gap-1"><Package className="w-3 h-3" />{it.fabricCode}</span>}
                        <span>× {it.qty || 1} {it.unit || "ad"}</span>
                      </div>
                    </div>
                  ))}
                  {(order.items || []).filter(it => !it.isAccessory).length > 4 && (
                    <div className="text-[11px] text-slate-400">+{(order.items||[]).filter(it=>!it.isAccessory).length-4} kalem daha</div>
                  )}
                  {order.notes && (
                    <div className="text-xs text-slate-500 italic border-t border-slate-200 dark:border-[#22304A] pt-2 mt-1">
                      Not: {order.notes}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-xs text-slate-400 italic">Sipariş bulunamadı ({appt.orderId})</div>
              )}
            </div>
          )}

          {/* Notlar */}
          {appt.notes && (
            <div className="zen-panel !p-3 bg-amber-50/50 dark:bg-amber-950/10 border border-amber-200/50 dark:border-amber-900/30">
              <div className="text-[10px] text-amber-600 dark:text-amber-400 font-bold uppercase mb-1">Not</div>
              <div className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{appt.notes}</div>
            </div>
          )}
        </div>
        {/* Footer aksiyonlar */}
        {showSnooze && (
          <div className="p-4 border-t border-amber-200/50 dark:border-amber-900/30 bg-amber-50/50 dark:bg-amber-950/10 space-y-3">
            <div className="text-xs font-bold text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
              <AlarmClock className="w-3.5 h-3.5" /> Randevuyu Ertele
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] font-semibold text-slate-500 mb-1">Yeni Tarih</label>
                <input type="date" value={snoozeDate} min={todayStr()}
                  onChange={e => setSnoozeDate(e.target.value)}
                  className="zen-input w-full" data-testid="snooze-date-input" />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-slate-500 mb-1">Erteleme Nedeni</label>
                <input value={snoozeReason} onChange={e => setSnoozeReason(e.target.value)}
                  placeholder="Müşteri uygun değil..." className="zen-input w-full" data-testid="snooze-reason-input" />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setShowSnooze(false); setSnoozeDate(""); setSnoozeReason(""); }}
                className="zen-btn-ghost !text-xs">Vazgeç</button>
              <button disabled={!snoozeDate || snoozeSaving}
                onClick={async () => {
                  setSnoozeSaving(true);
                  try { await onSnooze(appt.id, snoozeDate, snoozeReason); onClose(); }
                  finally { setSnoozeSaving(false); }
                }}
                className="zen-btn-primary !text-xs !bg-amber-500 hover:!bg-amber-600 !border-amber-500"
                data-testid="snooze-confirm-btn">
                {snoozeSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlarmClock className="w-3.5 h-3.5" />}
                Ertele
              </button>
            </div>
          </div>
        )}
        <div className="flex justify-between items-center p-4 border-t border-slate-200 dark:border-[#22304A]">
          <div className="flex gap-2">
            {isAdmin && (
              <button onClick={() => { onClose(); onDelete(appt.id); }}
                className="zen-btn-ghost text-rose-500 hover:text-rose-700 !text-xs"
                data-testid={`appt-detail-delete-${appt.id}`}>
                <Trash2 className="w-3.5 h-3.5" /> Sil
              </button>
            )}
            <button onClick={() => setShowSnooze(s => !s)}
              className={`zen-btn-ghost !text-xs ${showSnooze ? "text-amber-600 dark:text-amber-400" : "text-amber-500 hover:text-amber-700"}`}
              data-testid={`appt-snooze-btn-${appt.id}`}>
              <AlarmClock className="w-3.5 h-3.5" /> Ertele
            </button>
          </div>
          <button onClick={() => { onClose(); onEdit(appt); }}
            className="zen-btn-primary !text-xs"
            data-testid={`appt-detail-edit-${appt.id}`}>
            <Pencil className="w-3.5 h-3.5" /> Düzenle
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Sabah Özet Modalı ───────────────────────────────────────────────────────
function MorningSummaryModal({ today, onClose }) {
  const [todayAppts, setTodayAppts] = useState([]);
  const [postponedAppts, setPostponedAppts] = useState([]);
  const [loadingMorning, setLoadingMorning] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const past = new Date(); past.setMonth(past.getMonth() - 6);
        const future = new Date(); future.setMonth(future.getMonth() + 3);
        const { data } = await api.get(`/appointments?start=${toDateStr(past)}&end=${toDateStr(future)}`);
        const all = data || [];
        setTodayAppts(all.filter(a => a.date === today).sort((a,b) => (a.startTime||"").localeCompare(b.startTime||"")));
        setPostponedAppts(all.filter(a => a.status === "ertelendi").sort((a,b) => (a.date||"").localeCompare(b.date||"")));
      } catch(_) {}
      setLoadingMorning(false);
    };
    fetch();
  }, [today]);

  const dayLabel = new Date(`${today}T12:00:00`).toLocaleDateString("tr-TR", { weekday:"long", day:"numeric", month:"long" });

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="zen-panel w-full max-w-md max-h-[90vh] overflow-y-auto zen-anim-up">
        <div className="p-5 border-b border-slate-200 dark:border-[#22304A] flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <Sun className="w-5 h-5 text-amber-500" />
              <h3 className="font-extrabold text-base">Günlük Özet</h3>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 capitalize">{dayLabel}</p>
          </div>
          <button onClick={onClose} className="zen-btn-ghost !p-1.5"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 space-y-5">
          {loadingMorning ? (
            <div className="flex items-center justify-center gap-2 py-8 text-slate-400 text-sm">
              <Loader2 className="w-5 h-5 animate-spin" /> Yükleniyor...
            </div>
          ) : (
            <>
              {/* Bugünün Randevuları */}
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <CalendarDays className="w-4 h-4 text-sky-500" />
                  <span className="text-xs font-bold">Bugünün Randevuları</span>
                  {todayAppts.length > 0 && (
                    <span className="ml-auto text-[10px] font-bold bg-sky-500 text-white rounded-full px-2 py-0.5">{todayAppts.length}</span>
                  )}
                </div>
                {todayAppts.length === 0 ? (
                  <div className="text-xs text-slate-400 italic py-3 text-center bg-slate-50 dark:bg-slate-800/30 rounded-xl">Bugün randevu yok</div>
                ) : (
                  <div className="space-y-2">
                    {todayAppts.map(a => {
                      const tp = APPT_TYPE[a.type] || APPT_TYPE.genel;
                      const Icon = tp.icon;
                      return (
                        <div key={a.id} className={`rounded-xl p-3 ${tp.light} flex items-center gap-3`} data-testid={`morning-appt-${a.id}`}>
                          <Icon className="w-4 h-4 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-sm truncate">{a.customerName}</div>
                            <div className="text-xs opacity-70">{a.startTime} — {a.endTime} · {a.address || "Adres yok"}</div>
                          </div>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border shrink-0 ${tp.pill}`}>{tp.label}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Ertelenen Görevler */}
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <History className="w-4 h-4 text-rose-500" />
                  <span className="text-xs font-bold">Ertelenen Görevler</span>
                  {postponedAppts.length > 0 && (
                    <span className="ml-auto text-[10px] font-bold bg-rose-500 text-white rounded-full px-2 py-0.5">{postponedAppts.length}</span>
                  )}
                </div>
                {postponedAppts.length === 0 ? (
                  <div className="text-xs text-slate-400 italic py-3 text-center bg-slate-50 dark:bg-slate-800/30 rounded-xl">Ertelenmiş görev yok</div>
                ) : (
                  <div className="space-y-2">
                    {postponedAppts.map(a => {
                      const tp = APPT_TYPE[a.type] || APPT_TYPE.genel;
                      const dateLabel = a.date ? new Date(`${a.date}T12:00:00`).toLocaleDateString("tr-TR",{day:"numeric",month:"short",year:"numeric"}) : "—";
                      return (
                        <div key={a.id} className="rounded-xl p-3 bg-rose-50/70 dark:bg-rose-950/20 border border-rose-200/50 dark:border-rose-900/30 flex items-start gap-3" data-testid={`morning-postponed-${a.id}`}>
                          <AlarmClock className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-sm truncate">{a.customerName}</div>
                            <div className="text-xs text-slate-500">Yeni tarih: <strong>{dateLabel}</strong></div>
                            {a.snoozeReason && <div className="text-xs text-rose-600 dark:text-rose-400 italic mt-0.5">"{a.snoozeReason}"</div>}
                          </div>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border shrink-0 ${tp.pill}`}>{tp.label}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="p-4 border-t border-slate-200 dark:border-[#22304A]">
          <button onClick={onClose} className="w-full zen-btn-primary" data-testid="morning-close-btn">
            <Check className="w-4 h-4" /> Tamam, Güne Başla
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Ay Görünümü ─────────────────────────────────────────────────────────────
function MonthView({ year, month, apptsByDate, onDayClick, onApptClick, onDrop, today }) {
  const cells = useMemo(() => buildMonthGrid(year, month), [year, month]);
  const [dragOver, setDragOver] = useState(null);

  return (
    <div className="flex-1 overflow-hidden flex flex-col">
      {/* Gün başlıkları */}
      <div className="grid grid-cols-7 border-b border-slate-200 dark:border-[#22304A]">
        {WEEKDAYS.map(d => (
          <div key={d} className={`py-2 text-center text-[11px] font-bold uppercase text-slate-500 dark:text-slate-400
            ${d === "Cmt" || d === "Paz" ? "text-rose-400 dark:text-rose-500" : ""}`}>{d}</div>
        ))}
      </div>
      {/* Gün hücreleri */}
      <div className="grid grid-cols-7 flex-1 overflow-y-auto" style={{ gridAutoRows: "minmax(80px, 1fr)" }}>
        {cells.map((cell, idx) => {
          const ds = toDateStr(cell.date);
          const isToday = ds === today;
          const isWeekend = cell.date.getDay() === 0 || cell.date.getDay() === 6;
          const appts = apptsByDate[ds] || [];
          const isDrop = dragOver === ds;
          return (
            <div
              key={ds + idx}
              data-testid={`cal-day-${ds}`}
              onDragOver={e => { e.preventDefault(); setDragOver(ds); }}
              onDragLeave={() => setDragOver(null)}
              onDrop={e => { e.preventDefault(); setDragOver(null); onDrop(ds); }}
              onClick={() => onDayClick(ds)}
              className={`border-b border-r border-slate-200 dark:border-[#22304A] p-1 min-h-[80px] transition-colors
                ${!cell.cur ? "bg-slate-50/50 dark:bg-slate-900/30" : ""}
                ${isWeekend && cell.cur ? "bg-rose-50/20 dark:bg-rose-950/10" : ""}
                ${isDrop ? "bg-sky-50 dark:bg-sky-950/30 ring-inset ring-2 ring-sky-400" : ""}
                ${cell.cur ? "hover:bg-slate-50 dark:hover:bg-[#13203A]/50 cursor-pointer" : "cursor-default"}
              `}
            >
              <div className={`flex items-center justify-center w-6 h-6 rounded-full text-[12px] font-bold mb-1 transition-colors
                ${isToday ? "bg-[#E5322A] text-white shadow-md" : cell.cur ? "text-slate-700 dark:text-slate-200" : "text-slate-400 dark:text-slate-600"}`}>
                {cell.date.getDate()}
              </div>
              <div className="space-y-0.5">
                {appts.slice(0, 3).map(a => (
                  <ApptPill key={a.id} appt={a} onClick={onApptClick} onDragStart={() => {}} compact />
                ))}
                {appts.length > 3 && (
                  <div className="text-[10px] text-slate-400 pl-1 font-semibold">+{appts.length - 3} daha</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Hafta Görünümü ──────────────────────────────────────────────────────────
const HOURS = Array.from({length: 14}, (_, i) => `${String(i+7).padStart(2,"0")}:00`); // 07:00-20:00

function WeekView({ weekDays, apptsByDate, onApptClick, today }) {
  return (
    <div className="flex-1 overflow-auto">
      <div className="grid border-b border-slate-200 dark:border-[#22304A]" style={{ gridTemplateColumns: "48px repeat(7, 1fr)" }}>
        <div className="py-2" />
        {weekDays.map(d => {
          const ds = toDateStr(d);
          const isToday = ds === today;
          return (
            <div key={ds} className="py-2 text-center">
              <div className="text-[11px] text-slate-500 dark:text-slate-400 font-semibold">
                {WEEKDAYS[(d.getDay() + 6) % 7]}
              </div>
              <div className={`mx-auto mt-0.5 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold
                ${isToday ? "bg-[#E5322A] text-white" : "text-slate-700 dark:text-slate-200"}`}>
                {d.getDate()}
              </div>
            </div>
          );
        })}
      </div>
      <div className="grid" style={{ gridTemplateColumns: "48px repeat(7, 1fr)" }}>
        {/* Saat sütunu */}
        <div>
          {HOURS.map(h => (
            <div key={h} className="h-12 border-b border-slate-100 dark:border-[#1A2944] flex items-start justify-end pr-2 pt-0.5">
              <span className="text-[10px] text-slate-400 font-mono">{h}</span>
            </div>
          ))}
        </div>
        {/* Gün sütunları */}
        {weekDays.map(d => {
          const ds = toDateStr(d);
          const appts = apptsByDate[ds] || [];
          const isToday = ds === today;
          return (
            <div key={ds} className={`border-l border-slate-200 dark:border-[#22304A] relative
              ${isToday ? "bg-sky-50/30 dark:bg-sky-950/10" : ""}`}>
              {HOURS.map(h => (
                <div key={h} className="h-12 border-b border-slate-100 dark:border-[#1A2944]" />
              ))}
              {appts.map(a => {
                const [sh, sm] = (a.startTime || "09:00").split(":").map(Number);
                const [eh, em] = (a.endTime || "10:00").split(":").map(Number);
                const top = ((sh - 7) * 60 + sm) / 60 * 48;
                const height = Math.max(((eh - sh) * 60 + (em - sm)) / 60 * 48, 24);
                const st = APPT_STATUS[a.status] || APPT_STATUS.bekliyor;
                return (
                  <div
                    key={a.id}
                    data-testid={`week-appt-${a.id}`}
                    onClick={() => onApptClick(a)}
                    className={`absolute left-0.5 right-0.5 rounded-md px-1.5 py-0.5 cursor-pointer
                                text-[11px] font-semibold overflow-hidden hover:opacity-90 transition-opacity
                                ${st.light} border border-current/20`}
                    style={{ top, height, zIndex: 1 }}
                    title={`${a.startTime}-${a.endTime}: ${a.customerName}`}
                  >
                    <div className="truncate">{a.customerName}</div>
                    {height > 30 && <div className="truncate opacity-70">{a.startTime}</div>}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Ana Bileşen ─────────────────────────────────────────────────────────────
export default function AppointmentsPage() {
  const { user } = useContext(AuthCtx);
  const isAdmin = user?.role === "admin";

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("month"); // month | week
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingAppt, setEditingAppt] = useState(null);
  const [prefillDate, setPrefillDate] = useState(null);
  const [dragApptId, setDragApptId] = useState(null);
  const [showMorning, setShowMorning] = useState(false);

  const today = todayStr();
  const year  = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const weekDays = useMemo(() => buildWeekDays(currentDate), [currentDate]);

  // Randevuları yükle
  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Geniş tarih aralığı: 3 ay önce - 3 ay sonra
      const startOf = new Date(year, month - 1, 1);
      const endOf   = new Date(year, month + 2, 0);
      if (view === "week") {
        const ws = weekDays[0]; const we = weekDays[6];
        const { data } = await api.get(`/appointments?start=${toDateStr(ws)}&end=${toDateStr(we)}`);
        setAppointments(data);
      } else {
        const { data } = await api.get(`/appointments?start=${toDateStr(startOf)}&end=${toDateStr(endOf)}`);
        setAppointments(data);
      }
    } catch (e) {
      toast.error("Randevular yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [year, month, view, weekDays]);

  useEffect(() => { load(); }, [load]);

  // Sabah özeti — günlük ilk açılışta bir kez göster
  useEffect(() => {
    if (!user) return;
    const key = `zenMorningSummary_${todayStr()}`;
    if (!localStorage.getItem(key)) {
      const tid = setTimeout(() => setShowMorning(true), 1500);
      return () => clearTimeout(tid);
    }
  }, [user]);

  // Tarih → randevular map
  const apptsByDate = useMemo(() => {
    const m = {};
    appointments.forEach(a => {
      if (!m[a.date]) m[a.date] = [];
      m[a.date].push(a);
    });
    // Her günü saate göre sırala
    Object.values(m).forEach(arr => arr.sort((a, b) => (a.startTime || "").localeCompare(b.startTime || "")));
    return m;
  }, [appointments]);

  // Navigasyon
  const prev = () => {
    const d = new Date(currentDate);
    if (view === "month") d.setMonth(d.getMonth() - 1);
    else d.setDate(d.getDate() - 7);
    setCurrentDate(d);
  };
  const next = () => {
    const d = new Date(currentDate);
    if (view === "month") d.setMonth(d.getMonth() + 1);
    else d.setDate(d.getDate() + 7);
    setCurrentDate(d);
  };
  const goToday = () => setCurrentDate(new Date());

  // Gün hücresine tıklama → form açma
  const onDayClick = (ds) => {
    setPrefillDate(ds); setEditingAppt(null); setShowForm(true);
  };

  // Durum değiştirme
  const handleStatusChange = async (id, newStatus) => {
    await api.put(`/appointments/${id}`, { status: newStatus });
    setAppointments(prev => prev.map(a => a.id === id ? { ...a, status: newStatus } : a));
    if (selectedAppt?.id === id) setSelectedAppt(a => ({ ...a, status: newStatus }));
    toast.success(`Durum: ${APPT_STATUS[newStatus]?.label || newStatus}`);
  };

  // Ertele
  const handleSnooze = async (id, newDate, reason) => {
    await api.put(`/appointments/${id}`, { status: "ertelendi", date: newDate, snoozeReason: reason });
    setAppointments(prev => prev.map(a => a.id === id ? { ...a, status: "ertelendi", date: newDate, snoozeReason: reason } : a));
    toast.success(`Randevu ertelendi → ${newDate}`);
  };

  // Sil
  const handleDelete = async (id) => {    if (!window.confirm("Bu randevu silinsin mi?")) return;
    await api.delete(`/appointments/${id}`);
    setAppointments(p => p.filter(a => a.id !== id));
    setSelectedAppt(null);
    toast.success("Randevu silindi");
  };

  // Drag & drop ile tarih değiştir
  const handleDragStart = (id) => setDragApptId(id);
  const handleDrop = async (targetDate) => {
    if (!dragApptId) return;
    await api.put(`/appointments/${dragApptId}`, { date: targetDate });
    setAppointments(prev => prev.map(a => a.id === dragApptId ? { ...a, date: targetDate } : a));
    setDragApptId(null);
    toast.success("Randevu taşındı");
  };

  const headerLabel = view === "month"
    ? `${MONTHS_TR[month]} ${year}`
    : `${toDateStr(weekDays[0])} — ${toDateStr(weekDays[6])}`;

  // Bugünün randevuları özeti
  const todayAppts = apptsByDate[today] || [];

  return (
    <div className="flex flex-col zen-anim-up" style={{ height: "calc(100vh - 120px)" }}>
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-500 to-blue-700 flex items-center justify-center shadow-md">
            <CalendarDays className="w-5 h-5 text-white" />
          </span>
          <div>
            <h2 className="text-xl font-extrabold tracking-tight">Randevu Takvimi</h2>
            {todayAppts.length > 0 ? (
              <div className="text-[11px] text-slate-500">
                Bugün <strong className="text-[#E5322A]">{todayAppts.length}</strong> randevu
              </div>
            ) : (
              <div className="text-[11px] text-slate-400">
                Randevu eklemek için takvimde bir güne tıklayın veya “Yeni Randevu” kullanın
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Görünüm */}
          <div className="flex rounded-lg overflow-hidden border border-slate-200 dark:border-[#22304A]">
            {["month","week"].map(v => (
              <button key={v} onClick={() => setView(v)}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors
                  ${view === v ? "bg-[#E5322A] text-white" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-[#22304A]"}`}
                data-testid={`cal-view-${v}`}>
                {v === "month" ? "Ay" : "Hafta"}
              </button>
            ))}
          </div>
          {/* Navigasyon */}
          <div className="flex items-center gap-1">
            <button onClick={prev} className="zen-btn-ghost !p-2" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={goToday} className="zen-btn-ghost !px-3 text-xs font-semibold" data-testid="cal-today">Bugün</button>
            <button onClick={next} className="zen-btn-ghost !p-2" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <span className="text-sm font-bold text-slate-700 dark:text-slate-200 min-w-[160px] text-center">{headerLabel}</span>
          <button onClick={load} className="zen-btn-ghost !p-2" title="Yenile">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
          {/* Yeni Randevu */}
          <button onClick={() => { setEditingAppt(null); setPrefillDate(today); setShowForm(true); }}
            className="zen-btn-primary !text-xs" data-testid="new-appt-btn">
            <Plus className="w-4 h-4" /> Yeni Randevu
          </button>
          <button onClick={() => setShowMorning(true)}
            className="zen-btn-ghost !text-xs !p-2" title="Günlük Özeti Göster" data-testid="morning-summary-btn">
            <Sun className="w-4 h-4 text-amber-500" />
          </button>
        </div>
      </div>

      {/* Takvim ana alan */}
      <div className="zen-panel overflow-hidden flex flex-col flex-1">
        {loading ? (
          <div className="flex items-center justify-center flex-1 gap-2 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" /> Yükleniyor...
          </div>
        ) : view === "month" ? (
          <MonthView
            year={year} month={month} today={today}
            apptsByDate={apptsByDate}
            onDayClick={onDayClick}
            onApptClick={setSelectedAppt}
            onDrop={handleDrop}
          />
        ) : (
          <WeekView
            weekDays={weekDays} today={today}
            apptsByDate={apptsByDate}
            onApptClick={setSelectedAppt}
          />
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 mt-2 flex-wrap">
        {STATUS_ORDER.map(s => {
          const st = APPT_STATUS[s];
          return (
            <div key={s} className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 dark:text-slate-400">
              <span className={`w-2.5 h-2.5 rounded-full ${st.dot}`} />{st.label}
            </div>
          );
        })}
      </div>

      {/* Modallar */}
      {showMorning && (
        <MorningSummaryModal
          today={today}
          onClose={() => {
            localStorage.setItem(`zenMorningSummary_${today}`, "1");
            setShowMorning(false);
          }}
        />
      )}
      {selectedAppt && (
        <ApptDetailModal
          appt={selectedAppt}
          onClose={() => setSelectedAppt(null)}
          onEdit={a => { setEditingAppt(a); setPrefillDate(null); setShowForm(true); setSelectedAppt(null); }}
          onDelete={handleDelete}
          onStatusChange={handleStatusChange}
          onSnooze={handleSnooze}
          isAdmin={isAdmin}
        />
      )}
      <ApptFormModal
        open={showForm}
        onClose={() => { setShowForm(false); setEditingAppt(null); }}
        onSaved={() => { setShowForm(false); setEditingAppt(null); load(); }}
        editing={editingAppt}
        prefillDate={prefillDate}
      />
    </div>
  );
}

// ─── Global Alarm Checker (App.js'den çağrılır) ─────────────────────────────
export function useAppointmentAlarms(user) {
  const shownRef    = useRef(new Set(JSON.parse(localStorage.getItem("zenAlarmShown") || "[]")));
  const audioCtxRef = useRef(null);

  // AudioContext'i kullanıcı etkileşiminde oluştur ve kilidi aç (Autoplay Policy)
  useEffect(() => {
    const unlock = () => {
      try {
        if (!audioCtxRef.current) {
          audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        if (audioCtxRef.current.state === "suspended") {
          audioCtxRef.current.resume();
        }
      } catch (_) {}
      document.removeEventListener("click",   unlock);
      document.removeEventListener("keydown", unlock);
    };
    document.addEventListener("click",   unlock, { once: true });
    document.addEventListener("keydown", unlock, { once: true });
    return () => {
      document.removeEventListener("click",   unlock);
      document.removeEventListener("keydown", unlock);
    };
  }, []);

  // Güçlü sesli alarm — mevcut ctx kullan, her seferinde resume() çağır
  const playAlarm = useCallback(async (loud = false) => {
    try {
      // Bağlam yoksa oluştur (fallback)
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      // Tarayıcı politikası: askıda ise resume et
      if (ctx.state === "suspended") {
        await ctx.resume();
      }
      const beeps = loud ? 5 : 2;
      const gap   = 0.38;
      for (let i = 0; i < beeps; i++) {
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        // 2 frekanslı çift beep: daha dikkat çekici
        osc.frequency.value = loud ? (i % 2 === 0 ? 1047 : 880) : 880;
        const t0 = ctx.currentTime + i * gap;
        gain.gain.setValueAtTime(0, t0);
        gain.gain.linearRampToValueAtTime(loud ? 0.85 : 0.18, t0 + 0.02);
        gain.gain.setValueAtTime(loud ? 0.85 : 0.18, t0 + 0.22);
        gain.gain.linearRampToValueAtTime(0, t0 + 0.30);
        osc.start(t0); osc.stop(t0 + 0.32);
      }
    } catch (_) {}
  }, []);

  const checkAlarms = useCallback(async () => {
    if (!user) return;
    try {
      const { data: appts } = await api.get("/appointments/alarms");
      if (!Array.isArray(appts) || appts.length === 0) return;

      const now        = new Date();
      const tomorrowIso = toDateStr(new Date(now.getTime() + 86400000));

      appts.forEach(a => {
        const [h, m] = (a.startTime || "09:00").split(":").map(Number);
        const apptDateTime = new Date(`${a.date}T${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:00`);
        const diffMs   = apptDateTime.getTime() - now.getTime();
        const diffMins = diffMs / (1000 * 60);
        const tp       = APPT_TYPE[a.type] || APPT_TYPE.genel;

        // Randevunun özel alarm dakikaları (varsayılan: [120])
        const alarmMins = (a.alarmMinutes && a.alarmMinutes.length > 0) ? a.alarmMinutes : [120];
        alarmMins.forEach(alarmMin => {
          const keyAlarm = `alarm-${alarmMin}m-${a.id}-${a.date}`;
          // 7 dakikalık kontrol penceresi (5dk interval + buffer)
          if (diffMs > 0 && diffMins <= alarmMin && diffMins > alarmMin - 7 && !shownRef.current.has(keyAlarm)) {
            shownRef.current.add(keyAlarm);
            localStorage.setItem("zenAlarmShown", JSON.stringify([...shownRef.current]));
            const label = alarmMin < 60 ? `${alarmMin}dk` : alarmMin < 1440 ? `${alarmMin/60}sa` : "1 gün";
            toast.warning(`🔔 Randevu Alarmı — ${label} sonra: ${a.customerName}`, {
              description: `[${tp.label}] ${a.startTime} · ${a.address || "Adres yok"} · Telegram gönderildi`,
              duration: 25000,
            });
            playAlarm(true);
          }
        });

        // 1 gün öncesi bildirim
        const key1d = `1d-${a.id}-${a.date}`;
        if (a.date === tomorrowIso && !shownRef.current.has(key1d)) {
          shownRef.current.add(key1d);
          localStorage.setItem("zenAlarmShown", JSON.stringify([...shownRef.current]));
          toast.info(`📅 Yarın randevu var: ${a.customerName}`, {
            description: `${a.date} · ${a.startTime} · ${a.address || ""}`,
            duration: 15000,
          });
          playAlarm(false);
        }
      });
    } catch (_) {}
  }, [user, playAlarm]);

  useEffect(() => {
    if (!user) return;
    const tid      = setTimeout(checkAlarms, 3000);
    const interval = setInterval(checkAlarms, 5 * 60 * 1000);
    return () => { clearTimeout(tid); clearInterval(interval); };
  }, [user, checkAlarms]);
}
